import { useEffect, useState } from "react";
import { ImageIcon, ImageOff } from "lucide-react";
import {
  CERT_PREVIEW_SAMPLE_NAME,
  computeRecipientNameFontSize,
  formatCertificateDate,
  resolveNameFontFamily,
} from "../constants/certificateTemplateOptions";
import { resolveMediaUrl } from "../../../utils/resolveMediaUrl";

export default function CertificateLivePreview({
  imageUrl,
  purposeName,
  recipientName = CERT_PREVIEW_SAMPLE_NAME,
  matter,
  nameFontFamily = "serif",
  nameItalic = false,
  dateAlign = "center",
  showHeader = true,
}) {
  const displayName = String(recipientName || CERT_PREVIEW_SAMPLE_NAME).trim();
  const resolvedUrl = resolveMediaUrl(imageUrl);
  const [imageFailed, setImageFailed] = useState(false);
  const hasContent =
    String(purposeName || "").trim() ||
    displayName ||
    String(matter || "").trim();

  useEffect(() => {
    setImageFailed(false);
  }, [resolvedUrl]);

  const dateAlignClass =
    dateAlign === "left" || dateAlign === "right" ? dateAlign : "center";

  return (
    <div className="cert-templates-live-preview">
      {showHeader ? (
        <>
          <h4 className="cert-templates-live-preview__heading">Certificate preview</h4>
          <p className="cert-templates-live-preview__sub">
            Recipient name is supplied when generating certificates (sample name shown here).
          </p>
        </>
      ) : null}
      <div className="cert-templates-live-preview__frame" aria-live="polite">
        {resolvedUrl && !imageFailed ? (
          <img
            src={resolvedUrl}
            alt=""
            className="cert-templates-live-preview__bg"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="cert-templates-live-preview__bg-placeholder" aria-hidden>
            {resolvedUrl ? (
              <ImageOff size={40} strokeWidth={1.25} />
            ) : (
              <ImageIcon size={40} strokeWidth={1.25} />
            )}
            <span>
              {resolvedUrl
                ? "Template image could not be loaded"
                : "Upload a template image to preview background"}
            </span>
          </div>
        )}
        <div className="cert-templates-live-preview__overlay">
          <div className="cert-templates-live-preview__body">
            {String(purposeName || "").trim() ? (
              <p className="cert-templates-live-preview__purpose">{purposeName}</p>
            ) : null}
            {displayName ? (
              <p
                className="cert-templates-live-preview__name"
                style={{
                  fontFamily: resolveNameFontFamily(nameFontFamily),
                  fontSize: computeRecipientNameFontSize(displayName),
                  fontStyle: nameItalic ? "italic" : "normal",
                }}
              >
                {displayName}
              </p>
            ) : null}
            {String(matter || "").trim() ? (
              <p className="cert-templates-live-preview__matter">{matter}</p>
            ) : null}
            {!hasContent ? (
              <p className="cert-templates-live-preview__empty">
                Fill in the form on the left to see certificate text here.
              </p>
            ) : null}
          </div>
          <p
            className={`cert-templates-live-preview__date cert-templates-live-preview__date--${dateAlignClass}`}
          >
            {formatCertificateDate()}
          </p>
        </div>
      </div>
    </div>
  );
}
